import { TFunction } from 'i18next'

import { clearFundingCurrencySelection } from '@pages/Traders-Room/shared/hooks/useFundingCurrencySelector'

import { validateField } from '@utils/formValidation'
import { snakeToCamel } from '@utils/format.utils'

import type { PaymentProvider } from '@model/PaymentProviderDto'
import type { TradingAccountDetailDto } from '@model/TradingAccountDetailDto'
import type { WalletDto } from '@model/WalletDto'
import { isWalletFormFieldHiddenType } from '@model/WalletFormFieldType'
import { FormItemDto } from '@model/WalletPaymentProviderParametersDto'

import { DepositTarget } from '../hooks/types'
import type { DepositFormValues } from './types'

export const RESERVED_STEPS = ['amount', 'card-details', 'redirect', 'transaction'] as const

export const isRealPspId = (pspId?: string): pspId is string => {
  if (!pspId) return false
  return !(RESERVED_STEPS as readonly string[]).includes(pspId)
}

export const getDepositBasePath = (
  target: DepositTarget,
  id: string,
  dashboardContext: 'traders-room' | 'payment-agent' | string = 'traders-room',
  pspId?: string
) => {
  const base = `/dashboard/${dashboardContext}/deposit/${target}/${id}`
  return pspId ? `${base}/${pspId}` : base
}

export const buildFieldsFromForm = (form: PaymentProvider['parameters']['form']) => {
  return Object.fromEntries(
    (form ?? [])
      .filter((f) => !!f.field)
      .map((f) => {
        const key = f.grouping ? `${f.grouping}_${f.field}` : f.field!
        return [
          snakeToCamel(key),
          Array.isArray(f.value) ? f.value[0]?.value ?? '' : String(f.value ?? ''),
        ]
      })
  )
}

export const buildDepositInitialValues = (params: {
  target: DepositTarget
  destination: WalletDto | TradingAccountDetailDto
  paymentProvider: PaymentProvider
  dashboardContext: 'traders-room' | 'payment-agent'
  restoredValues?: DepositFormValues
}): DepositFormValues => {
  const { target, destination, paymentProvider, dashboardContext, restoredValues } = params

  const base: DepositFormValues = {
    dashboardContext,
    paymentProvider,
    amount: restoredValues?.amount,
    comment: restoredValues?.comment ?? '',
    fields:
      restoredValues?.fields ??
      clearFundingCurrencySelection(
        buildFieldsFromForm(paymentProvider.parameters.form),
        paymentProvider
      ),
    bonusCampaignId: restoredValues?.bonusCampaignId,
    isBonusSelected: restoredValues?.isBonusSelected ?? false,
    isBonusRulesAccepted: restoredValues?.isBonusRulesAccepted ?? false,
    terms: restoredValues?.terms ?? {
      depositing: false,
      paymentOperations: false,
      cupResponsibility: false,
      powerOfAttorney: false,
    },
    conversionRate: restoredValues?.conversionRate ?? 1,
    paymentAgent: restoredValues?.paymentAgent,
    availableBonusCampaigns: restoredValues?.availableBonusCampaigns,
  }

  if (target === 'wallet') {
    const w = destination as WalletDto
    return {
      ...base,
      wallet: {
        id: w.id,
        name: w.name,
        currency: { id: w.currency.id, name: w.currency.name },
        walletType: { id: w.walletType.id, name: w.walletType.name },
        depositAction: w.depositAction,
        balance: w.balance,
      },
    }
  }

  const ta = destination as TradingAccountDetailDto
  return {
    ...base,
    tradingAccount: {
      id: ta.id,
      name: String(ta.name ?? ''),
      wallet: { id: ta.wallet?.id ?? '', name: ta.wallet?.name ?? '' },
      currency: {
        id: ta.accountGroup?.currency?.id ?? '',
        name: ta.accountGroup?.currency?.name ?? '',
      },
      campaign: ta.campaign?.id
        ? {
            id: String(ta.campaign.id),
            name: ta.campaign.name,
            defaultCurrencyId: ta.campaign.defaultCurrencyId,
            minDepositAmount: ta.campaign.minDepositAmount,
            tradingAccountCurrencyMinDepositAmount:
              ta.campaign.tradingAccountCurrencyMinDepositAmount,
          }
        : null,
      balance: ta.balance,
      client: ta.client,
      company: ta.tickmillCompany,
      depositAction: ta.accountGroup?.isEnabled
        ? { canDeposit: true, availableToDeposit: null }
        : { canDeposit: false, availableToDeposit: null },
      isDemo: ta.accountGroup?.accountGroupType?.name !== 'Live',
    },
  }
}

const getFieldDetailName = (field: FormItemDto): string => {
  return field.grouping ? `${field.grouping}_${field.field}` : field.field
}

export const validatePaymentProviderFields = (
  form: FormItemDto[] = [],
  values: Record<string, unknown> = {},
  t: TFunction
): Record<string, string> => {
  return form.reduce<Record<string, string>>((errors, field) => {
    if (!field.clientFilled || isWalletFormFieldHiddenType(field.type)) {
      return errors
    }

    const fieldName = snakeToCamel(getFieldDetailName(field))
    const fieldValue = String(values[fieldName] ?? '')
    const error = validateField(t, field.mandatory, field.validationRegex)(fieldValue)

    if (error) {
      errors[fieldName] = error
    }

    return errors
  }, {})
}
